import { useCallback } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useMultiTenant } from "@/hooks/useMultiTenant";
import { GenericCombobox, type SearchResult } from "@/components/ui/generic-combobox";

interface Customer {
  id: string;
  legal_name: string;
  trade_name: string | null;
  document_number: string;
  name?: string;
}

interface CustomerComboboxProps {
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  allowClear?: boolean;
}

// Função para garantir que sempre há um nome a exibir
const getCustomerDisplayName = (customer: Customer): string => {
  // Prioridade: trade_name > legal_name > name (legado) > fallback
  if (customer.trade_name && customer.trade_name.trim()) {
    return customer.trade_name;
  }
  
  if (customer.legal_name && customer.legal_name.trim()) {
    // Ignorar valores gerados automaticamente
    if (!customer.legal_name.startsWith('Cliente (') && !customer.legal_name.startsWith('Empresa (')) {
      return customer.legal_name;
    }
  }
  
  // Fallback para campo legado 'name'
  if (customer.name && customer.name.trim()) {
    return customer.name;
  }
  
  // Último fallback: gerar nome baseado no tipo de documento
  const docLength = customer.document_number?.replace(/\D/g, '').length || 0;
  const type = docLength <= 11 ? 'CPF' : 'CNPJ';
  return `Cliente (${type}) - ${customer.document_number}`;
};

export function CustomerCombobox({ 
  value, 
  onChange, 
  error, 
  placeholder = "Selecione o cliente...",
  allowClear = false
}: CustomerComboboxProps) {
  const { companyId } = useMultiTenant();

  const searchFn = useCallback(async (params: {
    searchTerm: string;
    normalizedSearchTerm: string;
    page: number;
    pageSize: number;
  }): Promise<SearchResult<Customer>> => {
    if (!companyId) {
      return { data: [], totalCount: 0 };
    }

    try {
      const { data, error: rpcError } = await supabase.rpc('search_customers_fuzzy', {
        p_company_id: companyId,
        p_search_term: params.normalizedSearchTerm,
        p_limit: params.pageSize,
        p_offset: params.page * params.pageSize
      }) as { 
        data: Array<{ 
          id: string; 
          legal_name: string; 
          trade_name: string | null; 
          document_number: string; 
          name: string | null; 
          total_count: number 
        }> | null; 
        error: Error | null 
      };

      if (rpcError) throw rpcError;

      const results = (data || []).map(item => ({
        id: item.id,
        legal_name: item.legal_name,
        trade_name: item.trade_name,
        document_number: item.document_number,
        name: item.name || undefined
      }));

      const totalCount = data && data.length > 0 ? Number(data[0].total_count) : 0;

      return { data: results, totalCount };
    } catch (err) {
      console.error('Erro ao buscar clientes:', err);
      return { data: [], totalCount: 0 };
    }
  }, [companyId]);

  const initialFetchFn = useCallback(async (id: string): Promise<Customer | null> => {
    const { data } = await supabase
      .from('customers')
      .select('id, legal_name, trade_name, document_number, name')
      .eq('id', id)
      .single();
    if (!data) return null;
    return {
      id: data.id,
      legal_name: data.legal_name,
      trade_name: data.trade_name,
      document_number: data.document_number,
      name: data.name || undefined
    };
  }, []);

  const renderItem = useCallback((customer: Customer, isSelected: boolean) => (
    <>
      <Check
        className={cn(
          "mr-2 h-4 w-4 flex-shrink-0",
          isSelected ? "opacity-100" : "opacity-0"
        )}
      />
      <div className="flex flex-col flex-1 min-w-0">
        <span className="font-medium truncate">
          {getCustomerDisplayName(customer)}
        </span>
        <span className="text-xs text-muted-foreground truncate">
          {customer.document_number}
        </span>
      </div>
    </>
  ), []);

  return (
    <GenericCombobox
      value={value}
      onChange={onChange}
      searchFn={searchFn}
      initialFetchFn={initialFetchFn}
      getItemValue={(customer) => customer.id}
      getItemLabel={getCustomerDisplayName}
      getItemSubLabel={(customer) => customer.document_number}
      renderItem={renderItem}
      placeholder={placeholder}
      searchPlaceholder="Buscar cliente..."
      emptyMessage="Nenhum cliente encontrado."
      allowClear={allowClear}
      clearLabel="Nenhum cliente"
      error={error}
    />
  );
}
